import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { queryClient } from './lib/react-query/queryClient';
import { msalConfig } from './config/auth';
import { useAuthStore } from './lib/store/authStore';
import { env } from './config/env';
import App from './App';
import './index.css';

// Set document title from environment
document.title = env.appName;

// Component to initialize auth store after MSAL is ready
function AuthInitializer({ children }: { children: React.ReactNode }) {
  const initialize = useAuthStore((state) => state.initialize);

  useEffect(() => {
    // Initialize auth state (check if user is already logged in)
    // MSAL instance is already initialized and set in store by initializeApp()
    initialize();
  }, [initialize]);

  return <>{children}</>;
}

// Initialize MSAL and render app
async function initializeApp() {
  try {
    // Create MSAL instance
    const msalInstance = new PublicClientApplication(msalConfig);

    // IMPORTANT: Initialize MSAL before using any other MSAL API
    // This is required in MSAL Browser v3+
    await msalInstance.initialize();

    // Set MSAL instance in Zustand store
    useAuthStore.getState().setMsalInstance(msalInstance);

    // Render React app after MSAL is initialized
    ReactDOM.createRoot(document.getElementById('root')!).render(
      <React.StrictMode>
        <MsalProvider instance={msalInstance}>
          <QueryClientProvider client={queryClient}>
            <AuthInitializer>
              <App />
            </AuthInitializer>
          </QueryClientProvider>
        </MsalProvider>
      </React.StrictMode>
    );
  } catch (error) {
    console.error('Failed to initialize MSAL:', error);

    // Show error message to user
    document.getElementById('root')!.innerHTML = `
      <div style="
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100vh;
        font-family: system-ui, -apple-system, sans-serif;
        padding: 20px;
      ">
        <div style="
          max-width: 500px;
          padding: 24px;
          border: 1px solid #ef4444;
          border-radius: 8px;
          background: #fee;
        ">
          <h2 style="margin: 0 0 12px; color: #dc2626;">Authentication Error</h2>
          <p style="margin: 0; color: #991b1b;">
            Failed to initialize authentication. Please check your Azure configuration.
          </p>
          <details style="margin-top: 12px;">
            <summary style="cursor: pointer; color: #991b1b;">Details</summary>
            <pre style="margin-top: 8px; font-size: 12px; color: #7f1d1d; overflow: auto;">
${error instanceof Error ? error.message : String(error)}
            </pre>
          </details>
        </div>
      </div>
    `;
  }
}

// Start app initialization
initializeApp();
